<template>
  <b-modal
    id="manage-permission-modal"
    ref="managePermissionModal"
    title="Manage Permission"
    size="lg"
    hide-footer
    no-close-on-backdrop
    :visible="visible"
    @show="onShow"
  >
    <main-loader :loading="isLoading"></main-loader>
    <form @submit.prevent="saveRole" class="role-form-scroll">
      <!-- Permissions Section -->
      <div class="permissions-section-compact mt-3">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">
            Select Permissions
            <span class="badge badge-primary ml-2">
              {{ roleForm.permissions.length }} selected
            </span>
          </h6>
          <div>
            <button type="button" @click="selectAllPermissions" class="btn btn-xs btn-outline-primary mr-1">
              All
            </button>
            <button type="button" @click="clearAllPermissions" class="btn btn-xs btn-outline-secondary">
              Clear
            </button>
          </div>
        </div>
        <!-- Search Permissions -->
        <div class="form-group mb-3">
          <input
            type="text"
            v-model="permissionSearch"
            class="form-control"
            placeholder="Search permissions..."                  
          />
        </div>

        <!-- Permission Categories - Collapsible -->
        <div class="permission-list-compact">
          <div
            v-for="category in filteredPermissionCategories"
            :key="category.name"
            class="permission-category-compact"
            >
            <div class="category-title-compact" @click="toggleCategoryExpanded(category.name)" >
              <div 
              class="category-indicator"
              :class="isCategoryExpanded(category.name) ? 'expanded' : 'collapsed'"
              ></div>
              <strong class="text-dark">{{ category.label }}</strong>
              <span 
              class="badge ml-2 small"
              :class="getCategorySelectedCount(category.name) > 0 ? 'badge-primary' : 'badge-light'"
              >
              {{ getCategorySelectedCount(category.name) }}/{{ category.permissions.length }}
              </span>
              <button
              v-if="getCategorySelectedCount(category.name) < category.permissions.length"
              type="button"
              @click.stop="selectCategoryPermissions(category.name)"
              class="btn btn-xs btn-link ml-auto p-0 select-all-category"
              >
              Select all
              </button>
              <button
              v-else
              type="button"
              @click.stop="clearCategoryPermissions(category.name)"
              class="btn btn-xs btn-link ml-auto p-0 text-muted select-all-category"
              >
              Clear
              </button>
            </div>

            <div v-show="isCategoryExpanded(category.name)" class="category-checkboxes">
              <div
                v-for="permission in category.permissions"
                :key="permission.key"
                class="permission-checkbox-compact"
                >
                <div class="custom-control custom-checkbox">
                  <input
                  type="checkbox"
                  class="custom-control-input"
                  :id="`perm-${permission.key}`"
                  v-model="roleForm.permissions"
                  :value="permission.key"
                  />
                  <label class="custom-control-label" :for="`perm-${permission.key}`">
                    <span class="permission-name">{{ permission.label }}</span>
                    <small class="text-muted d-block permission-desc">{{ permission.description }}</small>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <!-- No results -->
          <div v-if="filteredPermissionCategories.length === 0" class="text-center py-3">
            <p class="text-muted mb-0">No permissions found</p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="d-flex justify-content-end mt-3 pt-3 border-top">
        <button type="button" @click="hideModal" class="btn btn-secondary mr-2" :disabled="savingRole">
          Cancel
        </button>
        <template v-if="$store.getters['authpermission/hasAnyPermission'](['roles.assign'])">
          <button type="submit" class="btn btn-primary" :disabled="savingRole">
            <span v-if="savingRole">
              <i class="fa fa-spinner fa-spin mr-2"></i>
              Updating...
            </span>
            <span v-else>
              <i class="fa fa-save mr-2"></i>
              Update Permission
            </span>
          </button>
        </template>
      </div>
    </form>   
  </b-modal>
</template>

<script>
import {
  GET_ROLE_WISE_PERMISSION_LIST,
  GET_ALL_PERMISSION,
  UPDATE_PERMISSION
} from "../../../store/actions.type.js";
const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;
export default {
  name: "ManagePermissionModal",
  components: {
    "main-loader": LoaderComponent,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    roleid: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
      return {
        roleDetails: [],
        permissionCategories: [],
        isLoading: false,
        permissionSearch: "",
        expandedCategories: [],
        savingRole: false,
        roleForm: {
          permissions: [],  
        }
      };
    },
  watch: {
    roleid: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.allPermissions();
          this.fetchRoleWisePermission();
        }
      },
      immediate: true,
    },
    filteredPermissionCategories: {
      immediate: true,
      handler(categories) {
        if (this.permissionSearch) {
          this.expandedCategories = categories.map(c => c.name);
        } else {
          this.expandedCategories = [];
        }
      }
    }
  },
  methods: {
    show() {
      this.$refs.managePermissionModal.show();
    },
    hideModal() {
      this.$refs.managePermissionModal.hide();
    },
    onShow() {
      // Optionally handle when modal is shown
    },
    getCategorySelectedCount(categoryName) {
      const categories = Array.isArray(this.permissionCategories)
        ? this.permissionCategories
        : [];
      const category = categories.find(c => c && c.name === categoryName);
      if (!category || !Array.isArray(category.permissions)) return 0;
      if (!this.roleForm || !Array.isArray(this.roleForm.permissions)) return 0;
      return category.permissions.filter(p =>
        this.roleForm.permissions.includes(p.key)
      ).length;
    },
    selectAllPermissions() {
      const categories = Array.isArray(this.permissionCategories)
        ? this.permissionCategories
        : [];
      const allPermissions = categories
        .flatMap(c => Array.isArray(c.permissions) ? c.permissions : [])
        .map(p => p.key);
      if (this.roleForm && Array.isArray(allPermissions)) {
        this.roleForm.permissions = allPermissions;
      }
      this.expandedCategories = categories.map(c => c.name);
    },
    clearAllPermissions() {
      if (this.roleForm) {
        this.roleForm.permissions = [];
      }
    },

    selectCategoryPermissions(categoryName) {
      const category = this.permissionCategories.find(c => c.name === categoryName);
      if (!category) return;

      const categoryPermKeys = category.permissions.map(p => p.key);
      // Add all permissions from this category
      this.roleForm.permissions = [
        ...new Set([...this.roleForm.permissions, ...categoryPermKeys])
      ];
    },

    clearCategoryPermissions(categoryName) {
      const category = this.permissionCategories.find(c => c.name === categoryName);
      if (!category) return;

      const categoryPermKeys = category.permissions.map(p => p.key);
      // Remove all permissions from this category
      this.roleForm.permissions = this.roleForm.permissions.filter(
        p => !categoryPermKeys.includes(p)
      );
    },

    toggleCategoryExpanded(categoryName) {
      const index = this.expandedCategories.indexOf(categoryName);
      if (index > -1) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(categoryName);
      }
    },
    
    isCategoryExpanded(categoryName) {
      return this.expandedCategories.includes(categoryName);
    },

    async fetchRoleWisePermission() {
      this.isLoading = true;
      this.setLoading && this.setLoading(true);

      try {
        this.roleDetails = [];
        const response = await this.$store.dispatch(
          GET_ROLE_WISE_PERMISSION_LIST,
          this.roleid,
        );
        this.roleDetails = response.data.details.map((item) => ({
          id: item.id,
          name: item.name,
          userCount: item.userCount,
          totalpermission: item.totalpermission,
          permissions: item.permissions 
        }));

        // Find the correct role details for the current roleid
        const currentRole = this.roleDetails.find(
          (role) => role.id === this.roleid
        );
        this.roleForm.permissions = currentRole && Array.isArray(currentRole.permissions)
          ? [...currentRole.permissions]
          : [];
      } catch (error) {
        console.error("Error fetching permission:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to fetch permission. Please try again.",
          { position: "top-right" },
        );
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
        this.setLoading && this.setLoading(false);
      }
    },

    async allPermissions() {
      this.isLoading = true;
      this.setLoading && this.setLoading(true);

      try {
        this.permissionCategories = [];
        const response = await this.$store.dispatch(
          GET_ALL_PERMISSION,
          this.roleid,
        );
        const data = response.data.details;
        if (data && data.length > 0) {
          this.permissionCategories = data;
        } 
        else {
            console.warn('No permissions returned from API, using default categories');
        }
      } catch (error) {
        console.error("Error fetching all permission:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to fetch all permission. Please try again.",
          { position: "top-right" },
        );
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
        this.setLoading && this.setLoading(false);
      }
    },

    async saveRole() {
      this.savingRole = true;
      try {
        const payload = {
          roleid: this.roleid,
          permissions: this.roleForm.permissions,
          createdBy: localStorage.getItem("userid"),
        };
        await this.$store.dispatch(UPDATE_PERMISSION, payload);
        this.$toast.success("Permissions updated successfully.", {
          position: "top-right",
        });
        this.hideModal();
        this.$emit("manage-permission");
      } catch (error) {
        console.error("Error updating permissions:", error);
        this.$toast.error(
          error.response?.data?.message ||
            "Failed to update permissions. Please try again.",
          { position: "top-right" },
        );
      } finally {
        this.savingRole = false;
      }
    },
  },

  computed: {
    filteredPermissionCategories() {
      if (!this.permissionSearch) {
        return this.permissionCategories;
      }
      const query = this.permissionSearch.toLowerCase();
      const categories = this.permissionCategories;
      return categories
        .map(category => ({
          ...category,
          permissions: category.permissions.filter(p =>
            p.label.toLowerCase().includes(query) ||
            p.description.toLowerCase().includes(query) ||
            p.key.toLowerCase().includes(query)
          )
        }))
        .filter(category => category.permissions.length > 0);
    }
  },
};
</script>
<style scoped>
/* Permissions Section - Compact */
.permissions-section-compact {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.permission-list-compact {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 5px;
}

.permission-list-compact::-webkit-scrollbar {
  width: 6px;
}

.permission-list-compact::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.permission-list-compact::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.permission-list-compact::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.permission-category-compact {
  margin-bottom: 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.permission-category-compact:last-child {
  margin-bottom: 0;
}

.category-title-compact {
  padding: 10px 12px;
  background: #fafafa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.category-title-compact:hover {
  background: #f0f0f0;
}

/* Category Indicator (Colored Dot) */
.category-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 10px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.category-indicator.collapsed {
  background: #dee2e6;
}

.category-indicator.expanded {
  background: #007bff;
}

.select-all-category {
  font-size: 11px;
  text-decoration: none;
  color: #007bff !important;
}

.select-all-category:hover {
  text-decoration: underline;
}

.category-checkboxes {
  padding: 10px 12px;
}

.permission-checkbox-compact {
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;
}

.permission-checkbox-compact:last-child {
  border-bottom: none;
}

.permission-checkbox-compact label {
  cursor: pointer;
  margin-bottom: 0;
  font-size: 14px;
}

.permission-checkbox-compact label small {
  font-size: 12px;
  margin-top: 2px;
}

.permission-checkbox-compact:hover {
  background: #fafafa;
  border-radius: 4px;
}

.permission-name {
  font-weight: 500;
}

.permission-desc {
  line-height: 1.3;
}

/* Extra small button */
.btn-xs {
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1.2;
  border-radius: 0.2rem;
}

/* Badge */
.badge {
  padding: 0.4em 0.8em;
  font-weight: 500;
}

/* Border utilities */
.border-left-0 {
  border-left: 0 !important;
}

.border-right-0 {
  border-right: 0 !important;
}

/* Scrollbar for permissions section */
.permissions-section::-webkit-scrollbar {
  width: 8px;
}

.permissions-section::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.permissions-section::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.permissions-section::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>